import _ from 'underscore';
import React, { useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Carousel } from 'react-responsive-carousel';
import { leftIcon, rightIcon, ResponsiveMode, THEME_MODE_KEY_MAPPING } from '@bingads-webui-campaign-react/smart-component-util';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { CARD_DIALOG_MODE } from './constants';

const getStyles = (component, { palette = {} }) => ({
  page: {
    display: 'flex',
    justifyContent: 'center',
  },

  carouselContainer: {
    '& .carousel.carousel-slider': {
      overflow: 'unset',
      '& .slide': {
        background: palette.primaryBackground,
        textAlign: 'left',
      },
      '& .control-arrow': {
        width: 16,
        height: 16,
        lineHeight: 1,
        top: '50%',
        marginTop: -30,
        '&:before': {
          content: 'none',
        },
        '&:after': {
          content: '""',
          width: 16,
          height: 16,
          backgroundSize: '16px 16px',
          marginTop: -5,
          marginLeft: -5,
          display: 'block',
          ...(palette.mode === THEME_MODE_KEY_MAPPING.Dark ? { filter: 'brightness(0) invert(1)' } : {}),
        },
        display: 'block',
      },
      '& .control-next': {
        opacity: 1,
        background: 'transparent',
        backgroundSize: '16px 16px',
        '&:hover, &:active': {
          backgroundColor: palette.secondaryBackground,
        },
        '&:focus': {
          outline: `1px solid ${palette.pageControl}`,
        },
        '&:after': {
          backgroundImage: `url(${rightIcon})`,
        },
      },
      '& .control-prev': {
        opacity: 1,
        background: 'transparent',
        backgroundSize: '16px 16px',
        '&:hover, &:active': {
          backgroundColor: palette.secondaryBackground,
        },
        '&:focus': {
          outline: `1px solid ${palette.pageControl}`,
        },
        '&:after': {
          backgroundImage: `url(${leftIcon})`,
        },
      },
      '& .control-arrow.control-disabled': {
        display: 'none',
      },
      '& .control-dots': {
        paddingLeft: 0,
        '& .dot': {
          background: 'transparent',
          border: `1px solid ${palette.pageControl}`,
          boxShadow: '0px 0px 0px',
        },
        '& .dot.selected': {
          background: palette.pageSelect,
        },
      },
    },
  },
  mobileContainer: {
    '& .carousel .control-dots .dot': {
      margin: '0 4px',
    },
  },
});

const RESPONSIVE_MODE_TO_ITEMS_PER_PAGE = {
  [ResponsiveMode.LargeDesktop]: 3,
  [ResponsiveMode.MediumDesktop]: 2,
  [ResponsiveMode.SmallDesktop]: 1,
  [ResponsiveMode.Mobile]: 1,
};

const mapCardsToPage = (items, startingIndex, itemsPerPage, CardComponent, i18n) => {
  const page = [];

  if (_.isEmpty(items)) {
    return page;
  }
  let currentIndex = startingIndex;

  while (currentIndex < items.length && currentIndex < startingIndex + itemsPerPage) {
    const item = items[currentIndex];
    page.push((
      <div key={item.id || item.name}>
        <CardComponent
          item={item}
          i18n={i18n}
          mode={CARD_DIALOG_MODE.VIEW_MODE}
        />
      </div>
    ));

    currentIndex += 1;
  }

  return page;
};

const getNumberOfPages = (items, responsiveSize) => {
  const itemsPerPage = RESPONSIVE_MODE_TO_ITEMS_PER_PAGE[responsiveSize];

  return Math.ceil(items.length / itemsPerPage);
};

const mapCards = (items, responsiveSize, classes, CardComponent, i18n) => {
  const itemsPerPage = RESPONSIVE_MODE_TO_ITEMS_PER_PAGE[responsiveSize];

  const carouselPages = [];
  if (_.isEmpty(items)) {
    return carouselPages;
  }
  const numPages = getNumberOfPages(items, responsiveSize);

  for (let index = 0; index < numPages; index += 1) {
    carouselPages.push((
      <div className={classes.page} key={index}>
        {mapCardsToPage(items, index * itemsPerPage, itemsPerPage, CardComponent, i18n)}
      </div>
    ));
  }

  return carouselPages;
};

export const UnStyledItemCarousel = ({
  classes,
  items,
  responsiveSize,
  CardComponent,
  i18n,
}) => {
  const [carouselCursor, setCarouselCursor] = useState(0);
  const updateCarouselCursor = useCallback((index) => {
    setCarouselCursor(index);
  }, []);
  const numPages = getNumberOfPages(items, responsiveSize);

  useEffect(() => {
    if (carouselCursor >= numPages) {
      setCarouselCursor(carouselCursor - 1);
    }
  }, [numPages, carouselCursor, setCarouselCursor]);

  // TODO: multi-channel need to update react-responsive-carousel to latest version (>=3.1.55)
  // to get following accessibility tweak.
  const ariaLabels = {
    leftArrow: i18n.getString(_TL_('Previous page')),
    rightArrow: i18n.getString(_TL_('Next page')),
    item: i18n.getString(_TL_('Page')),
  };

  return (
    <div
      className={
        classnames(
          classes.carouselContainer,
          responsiveSize === ResponsiveMode.Mobile && classes.mobileContainer,
          'smart-carousel-container'
        )
      }
    >
      <Carousel
        showThumbs={false}
        showStatus={false}
        axis="horizontal"
        autoPlay={false}
        autoFocus={false}
        useKeyboardArrows
        selectedItem={carouselCursor}
        onClickItem={updateCarouselCursor}
        onChange={updateCarouselCursor}
        showArrows={numPages > 1}
        showIndicators={numPages > 1}
        labels={ariaLabels}
      >
        {mapCards(items, responsiveSize, classes, CardComponent, i18n)}
      </Carousel>
    </div>
  );
};

UnStyledItemCarousel.propTypes = {
  classes: PropTypes.shape({
    page: PropTypes.string.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })),
  responsiveSize: PropTypes.string.isRequired,
  CardComponent: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
};

UnStyledItemCarousel.defaultProps = {
  items: [],
};

UnStyledItemCarousel.displayName = 'ItemCarousel';

export const ItemCarousel = withDefaultStyles(UnStyledItemCarousel, getStyles);
